/**
 * SidePanel — Container that displays the active activity's content
 */
import React, { Suspense, useState } from 'react';
import {
  Box,
  Typography,
  IconButton,
  Tooltip,
  CircularProgress,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
} from '@mui/material';
import {
  Close,
  MoreHoriz,
  UploadFile,
  Print,
  TableChart,
  LayersClear,
  Help,
} from '@mui/icons-material';
import { tokens } from '../../theme';
import { useGeoveraTheme } from '../../theme/ThemeContext';
import type { ActivityId } from './AppShell';
import LayersPanel from './panels/LayersPanel';
import SymbologyPanel from './panels/SymbologyPanel';
import AnalysisPanel from './panels/AnalysisPanel';
import EditPanel from './panels/EditPanel';
import DrawingPanel from './panels/DrawingPanel';
import MeasurePanel from './panels/MeasurePanel';
import BookmarksActivityPanel from './panels/BookmarksActivityPanel';
import SearchPanel from './panels/SearchPanel';
import CRSPanel from './panels/CRSPanel';
import PrintPanel from './panels/PrintPanel';
import SurveyPanel from './panels/SurveyPanel';
import DesignPanel from './panels/DesignPanel';
import TerrainPanel from './panels/TerrainPanel';
import FieldPanel from './panels/FieldPanel';

interface SidePanelProps {
  activity: ActivityId;
  width: number;
  onClose: () => void;
}

const ACTIVITY_TITLES: Record<NonNullable<ActivityId>, string> = {
  layers: 'Layers',
  symbology: 'Symbology',
  analysis: 'Spatial Analysis',
  edit: 'Edit',
  drawing: 'Drawing & Annotation',
  measure: 'Measure',
  survey: 'Survey & COGO',
  design: 'Design & Civil Engineering',
  terrain: 'Terrain, Contours & 3D',
  field: 'Field Collection & GNSS',
  bookmarks: 'Bookmarks',
  search: 'Search',
  crs: 'Coordinate System',
  print: 'Print & Export',
};

const PanelLoader: React.FC = () => {
  const { palette } = useGeoveraTheme();
  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        height: 200,
      }}
    >
      <CircularProgress size={24} sx={{ color: palette.accent.primary }} />
    </Box>
  );
};

const SidePanel: React.FC<SidePanelProps> = ({ activity, width, onClose }) => {
  const { palette } = useGeoveraTheme();
  const [menuAnchor, setMenuAnchor] = useState<null | HTMLElement>(null);

  if (!activity) return null;

  const title = ACTIVITY_TITLES[activity];

  const renderContent = () => {
    switch (activity) {
      case 'layers':
        return <LayersPanel />;
      case 'symbology':
        return <SymbologyPanel />;
      case 'analysis':
        return <AnalysisPanel />;
      case 'edit':
        return <EditPanel />;
      case 'drawing':
        return <DrawingPanel />;
      case 'measure':
        return <MeasurePanel />;
      case 'bookmarks':
        return <BookmarksActivityPanel />;
      case 'search':
        return <SearchPanel />;
      case 'crs':
        return <CRSPanel />;
      case 'print':
        return <PrintPanel />;
      case 'survey':
        return <SurveyPanel />;
      case 'design':
        return <DesignPanel />;
      case 'terrain':
        return <TerrainPanel />;
      case 'field':
        return <FieldPanel />;
      default:
        return null;
    }
  };

  const handleMenuAction = (action: string) => {
    setMenuAnchor(null);
    switch (action) {
      case 'import':
        window.dispatchEvent(new CustomEvent('geovera:open-import'));
        break;
      case 'attributes':
        window.dispatchEvent(new CustomEvent('geovera:open-attributes'));
        break;
      case 'print':
        window.dispatchEvent(new CustomEvent('geovera:open-print'));
        break;
      case 'close':
        onClose();
        break;
      case 'help':
        window.open('/digipin-encoder', '_blank', 'noopener,noreferrer');
        break;
      default:
        break;
    }
  };

  return (
    <Box
      sx={{
        width: { xs: `calc(100vw - ${tokens.layout.activityBarWidth}px)`, sm: width },
        minWidth: { xs: `calc(100vw - ${tokens.layout.activityBarWidth}px)`, sm: width },
        position: { xs: 'absolute', sm: 'static' },
        left: { xs: tokens.layout.activityBarWidth, sm: 'auto' },
        zIndex: { xs: 100, sm: 'auto' },
        flexShrink: 0,
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        backgroundColor: palette.bg.elevated,
        borderRight: `1px solid ${palette.border.base}`,
        overflow: 'hidden',
      }}
    >
      <Box
        sx={{
          height: 36,
          minHeight: 36,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          px: 1.5,
          flexShrink: 0,
          borderBottom: `1px solid ${palette.border.subtle}`,
        }}
      >
        <Typography
          sx={{
            color: palette.text.secondary,
            fontSize: tokens.fontSize.sm,
            fontWeight: 600,
            textTransform: 'uppercase',
            letterSpacing: '0.8px',
          }}
        >
          {title}
        </Typography>
        <Box sx={{ display: 'flex', gap: 0.25 }}>
          <Tooltip title="More actions" arrow>
            <IconButton
              size="small"
              onClick={(e) => setMenuAnchor(e.currentTarget)}
              sx={{
                width: 22,
                height: 22,
                color: palette.text.muted,
                '&:hover': { color: palette.text.primary },
              }}
              aria-label="More actions"
            >
              <MoreHoriz sx={{ fontSize: 16 }} />
            </IconButton>
          </Tooltip>
          <Tooltip title="Close panel" arrow>
            <IconButton
              size="small"
              onClick={onClose}
              sx={{
                width: 22,
                height: 22,
                color: palette.text.muted,
                '&:hover': { color: palette.text.primary },
              }}
              aria-label="Close panel"
            >
              <Close sx={{ fontSize: 16 }} />
            </IconButton>
          </Tooltip>
        </Box>
      </Box>

      <Menu
        anchorEl={menuAnchor}
        open={Boolean(menuAnchor)}
        onClose={() => setMenuAnchor(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <MenuItem onClick={() => handleMenuAction('import')}>
          <ListItemIcon>
            <UploadFile fontSize="small" />
          </ListItemIcon>
          <ListItemText>Add data</ListItemText>
        </MenuItem>
        <MenuItem onClick={() => handleMenuAction('attributes')}>
          <ListItemIcon>
            <TableChart fontSize="small" />
          </ListItemIcon>
          <ListItemText>Attribute table</ListItemText>
        </MenuItem>
        <MenuItem onClick={() => handleMenuAction('print')}>
          <ListItemIcon>
            <Print fontSize="small" />
          </ListItemIcon>
          <ListItemText>Print & export</ListItemText>
        </MenuItem>
        <MenuItem onClick={() => handleMenuAction('help')}>
          <ListItemIcon>
            <Help fontSize="small" />
          </ListItemIcon>
          <ListItemText>Help</ListItemText>
        </MenuItem>
        <MenuItem onClick={() => handleMenuAction('close')}>
          <ListItemIcon>
            <LayersClear fontSize="small" />
          </ListItemIcon>
          <ListItemText>Close panel</ListItemText>
        </MenuItem>
      </Menu>

      <Box
        sx={{
          flex: 1,
          overflow: 'auto',
          minHeight: 0,
        }}
      >
        <Suspense fallback={<PanelLoader />}>{renderContent()}</Suspense>
      </Box>
    </Box>
  );
};

export default SidePanel;
